import type { GetStaticPaths, GetStaticProps } from 'next';
import type { SingleStrain } from 'lib/queries/strains';
import type { FigureProps } from 'components/figure';
import type { Cannabinoid } from 'types/strains';
import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Card, Link, Paragraph, Wrap } from '@leafwell/components';
import Loader from 'components/loader';
import PageWrap from 'components/page-wrap';
import HeadingBody from 'components/heading-body';
import StrainCardRest from 'components/strain-card-rest';
import StrainTypes from 'components/strain-types';
import Breadcrumbs from 'components/breadcrumbs';
import WysiwygContent from 'components/wysiwyg-content';
import Figure from 'components/figure';
import Navbar, { NavbarProps } from 'components/navbar';
import LineCounter from 'components/line-counter';
import CannabisCounselingCta from 'components/cannabis-counseling-cta';
import Faqs from 'components/faqs';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { getSingleStrain } from 'lib/queries/strains';

export type StrainSections = { sections: NavbarProps['items'] };

const StrainNavbar: React.FC<StrainSections> = ({ sections }) => {
  const observable = useMemo(() => {
    if (!sections || typeof document === 'undefined') {
      return sections;
    }

    return sections
      ?.map(({ id, ...item }) => {
        const anchor = document.getElementById(id);
        const firstChild = anchor?.firstElementChild;

        if (firstChild || anchor) {
          return {
            ...item,
            id,
            node: firstChild || anchor,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [sections, typeof document]);

  return (
    <Navbar
      {...{
        items: observable,
        size: 'default',
        variant: 'border',
        innerNav: true,
      }}
    />
  );
};

const ListSlash: React.FC<{
  items: string[];
}> = ({ items }) => (
  <ul {...{ className: 'flex flex-wrap' }}>
    {items.map((name, index) => (
      <React.Fragment {...{ key: index }}>
        <HeadingBody
          {...{
            level: '3',
            tagName: 'li',
            title: name,
          }}
        />
        {index < items.length - 1 && (
          <HeadingBody
            {...{
              'aria-hidden': 'true',
              className: 'text-gray-400 px-2',
              level: '3',
              tagName: 'li',
              title: '/',
            }}
          />
        )}
      </React.Fragment>
    ))}
  </ul>
);

const StrainAromas: React.FC<{
  items: string[];
  title: string;
}> = ({ items, title }) => (
  <section>
    <HeadingBody
      {...{
        className: 'w-full',
        level: '5',
        tagName: 'h3',
        title,
      }}
    />
    <ListSlash {...{ items }} />
  </section>
);

const StrainCannabinoid: React.FC<Cannabinoid> = ({
  abbreviation,
  name,
  quantityStart,
  quantityEnd,
  extraInfo,
}) => {
  return (
    <Card
      {...{
        className: 'lg:gap-y-12 h-56 w-80 md:h-60 lg:w-100',
        filled: true,
        rounded: true,
        variant: 'light',
      }}
    >
      <header>
        <Paragraph {...{ size: 'small' }}>{name}</Paragraph>
        <HeadingBody
          {...{
            level: '4',
            tagName: 'h3',
            title: abbreviation,
          }}
        />
      </header>
      <p
        {...{
          className: 'grid self-end',
        }}
      >
        <HeadingBody
          {...{
            level: '2',
            tagName: 'span',
            title: `${quantityStart === null ? '0' : `${quantityStart}%`}${
              quantityEnd && quantityEnd !== quantityStart
                ? `-${quantityEnd}%`
                : ''
            }`,
          }}
        />
        {extraInfo && (
          <Paragraph {...{ size: 'small', tagName: 'span' }}>
            {extraInfo}
          </Paragraph>
        )}
      </p>
    </Card>
  );
};

type StrainIconProps = {
  icon: Pick<FigureProps, 'src' | 'width' | 'height' | 'alt'>;
};

const StrainIcon: React.FC<StrainIconProps> = ({ icon }) => (
  <div
    {...{
      className:
        'rounded-full w-12 h-12 p-1 bg-gradient-to-b from-green-300/50 to-white',
    }}
  >
    <Figure
      {...{
        ...icon,
        className: 'rounded-full bg-white p-2 h-full',
        objectFit: 'contain',
      }}
    />
  </div>
);

const StrainInfo: React.FC<
  StrainIconProps & { content: string; label: string }
> = ({ content, icon, label }) => (
  <section {...{ className: 'grid gap-y-3 items-start' }}>
    <StrainIcon {...{ icon }} />
    <Paragraph {...{ tagName: 'h4', size: 'large' }}>{label}</Paragraph>
    <WysiwygContent
      {...{
        content,
      }}
    />
  </section>
);

const StrainContent: React.FC<SingleStrain> = ({
  strain: {
    aroma,
    content,
    link,
    acf: {
      cannabinoids,
      composition,
      conditions,
      conditionsDescription,
      dosing,
      effectsExtraInfo,
      faqs,
      flavonoids,
      growing,
      growingFlowering,
      growingHeight,
      growingHumidity,
      growingImage,
      thc,
      terpenesDescription,
      cannabisCounselingCta,
      wysiwygTemplate,
    },
    negativeEffect,
    featuredMedia,
    flavor,
    seo,
    positiveEffect,
    title,
    strainType,
    terpene,
  },
  relatedStrains,
}) => {
  const { strainsLandingPage, translations } = useWordPressContext();
  const hasAromas = aroma?.length > 0;
  const hasComposition = composition?.length > 0;
  const hasFlavors = flavor?.length > 0;
  const { cannabisCounselingCtaPosition } = cannabisCounselingCta;

  const sections = useMemo(() => {
    const strainSections = [
      { name: translations['overview'], id: 'overview' },
      { name: translations['effects'], id: 'effects' },
      {
        name: translations['medical-applications'],
        id: 'medical-applications',
      },
      { name: translations['dosing'], id: 'dosing' },
      { name: translations['growing'], id: 'growing' },
    ];
    if (faqs?.length > 0) {
      strainSections.push({ name: translations['faqs'], id: 'faqs' });
    }
    return strainSections.map(item => ({ ...item, uri: `#${item.id}` }));
  }, [translations, faqs]);

  return wysiwygTemplate && wysiwygTemplate.length > 0 ? (
    <PageWrap seo={seo} title={title}>
      <Wrap size="large" className="items-center justify-center">
        <WysiwygContent {...{ content: wysiwygTemplate }} />
      </Wrap>
    </PageWrap>
  ) : (
    <PageWrap seo={seo} title={title}>
      <Wrap
        {...{
          className: 'md:grid-cols-2 lg:grid-cols-3 md:gap-x-10',
          size: 'large',
          tagName: 'header',
        }}
      >
        <Breadcrumbs
          {...{
            className: 'col-span-full',
            items: [
              {
                href: strainsLandingPage?.uri,
                label: strainsLandingPage?.title,
              },
              {
                href: link,
                label: title,
              },
            ],
          }}
        />
        {featuredMedia && (
          <Figure
            className="max-w-xs mx-auto"
            priority={true}
            objectFit="contain"
            {...featuredMedia}
          />
        )}
        <div {...{ className: 'lg:col-span-2 grid gap-3' }}>
          <StrainTypes
            {...{ nodes: strainType, linkable: true, variant: 'default' }}
          />
          <HeadingBody
            {...{
              level: '2',
              tagName: 'h1',
              title,
            }}
          />
          <WysiwygContent {...{ className: 'lg:columns-2 gap-10', content }} />
        </div>
      </Wrap>
      {cannabisCounselingCtaPosition === 'top' && (
        <CannabisCounselingCta {...{ ...cannabisCounselingCta }} />
      )}
      <StrainNavbar {...{ sections }} />
      <Wrap
        {...{
          className: '!flex !flex-wrap !justify-between !items-end',
          id: sections[0].id,
          size: 'large',
        }}
      >
        <span>
          <HeadingBody
            {...{
              className: '!mb-2',
              level: '5',
              tagName: 'h2',
              title: translations['Cannabinoids'],
            }}
          />
          <StrainCannabinoid
            {...{
              ...thc,
              abbreviation: translations['THC'],
              name: translations['Tetrahydrocannabinol'],
            }}
          />
        </span>
        {cannabinoids &&
          cannabinoids instanceof Array &&
          cannabinoids?.map((cannabinoid, index) => (
            <StrainCannabinoid
              {...{
                ...cannabinoid,
                key: index,
              }}
            />
          ))}
        {hasComposition && (
          <div>
            <HeadingBody
              {...{
                level: '5',
                tagName: 'h2',
                className: '!mb-2',
                title: translations['Strain types'],
              }}
            />
            <Card
              {...{
                rounded: true,
                tagName: 'div',
                className: 'h-56 w-80 md:h-60 lg:w-100 gap-3',
              }}
            >
              <Image
                className="ml-auto h-10"
                height={48}
                loading="lazy"
                src="/chart.svg"
                width={48}
                alt=""
              />
              <ul
                {...{
                  className: 'grid gap-y-3',
                }}
              >
                {composition.map(({ quantity, type }, index) => (
                  <li {...{ key: index }}>
                    <HeadingBody
                      {...{
                        level: '4',
                        tagName: 'span',
                        title: `${quantity === null ? 0 : quantity}%`,
                      }}
                    />
                    <LineCounter
                      {...{
                        className: 'col-span-full',
                        color: index % 2 === 0 ? 'lilac-300' : 'green',
                        count: quantity === null ? 0 : quantity,
                        total: 100,
                      }}
                    />
                    <Paragraph {...{ size: 'small', tagName: 'span' }}>
                      {type}
                    </Paragraph>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}
      </Wrap>
      <Wrap
        {...{
          className: 'gap-y-6 lg:gap-y-6',
          size: 'large',
        }}
      >
        {hasAromas && (
          <StrainAromas {...{ title: translations['Aromas'], items: aroma }} />
        )}
        {hasFlavors && (
          <StrainAromas
            {...{ title: translations['Flavors'], items: flavor }}
          />
        )}
      </Wrap>
      <Wrap
        {...{
          className:
            'md:grid-cols-2 lg:grid-cols-3 sm:gap-x-10 gap-y-6 lg:gap-y-6',
          size: 'large',
        }}
      >
        <header>
          <HeadingBody
            {...{
              className: 'col-span-full pb-6',
              level: '5',
              tagName: 'h3',
              title: translations['Terpenes'],
            }}
          />
          <Paragraph
            {...{
              dangerouslySetInnerHTML: { __html: terpenesDescription },
              size: 'small',
              tagName: 'article',
              variant: 'medium',
            }}
          />
        </header>
        <ul
          {...{
            className:
              'grid sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 lg:col-span-2',
          }}
        >
          {terpene?.map(({ name, color }, index) => (
            <Card
              {...{
                className: 'h-36',
                key: index,
                rounded: true,
                size: 'small',
                tagName: 'li',
              }}
              style={{
                background: `linear-gradient(90deg, ${
                  color ? color : '#000'
                } 5%, #fff 5%)`,
              }}
            >
              <Paragraph {...{ className: 'self-end ml-5' }}>{name}</Paragraph>
            </Card>
          ))}
        </ul>
      </Wrap>
      <Wrap
        {...{
          className: 'gap-y-3 lg:gap-y-3',
          size: 'large',
        }}
      >
        <HeadingBody
          {...{
            className: 'w-full',
            level: '5',
            tagName: 'h2',
            title: translations['Flavonoids'],
          }}
        />
        <HeadingBody
          {...{
            className: 'lg:leading-relaxed prose-links-icon',
            level: '3',
            tagName: 'article',
            title: flavonoids,
          }}
        />
      </Wrap>
      <Wrap
        {...{
          id: sections[1].id,
          size: 'large',
        }}
      >
        <HeadingBody
          {...{
            level: '4',
            tagName: 'h2',
            title: sections[1].name,
          }}
        />
        {positiveEffect?.length > 0 && (
          <article>
            <HeadingBody
              {...{
                className: 'max-w-lg',
                level: '4',
                tagName: 'p',
                title: translations[
                  '%s has been reported to have the following effects:'
                ].replace('%', title),
              }}
            />
            <ul>
              {positiveEffect.map((name, index) => (
                <li
                  {...{
                    className:
                      'flex items-center gap-2 border-b border-indigo-200 pt-5 pb-3',
                    key: index,
                  }}
                >
                  <HeadingBody
                    {...{
                      className: 'leading-none',
                      level: '2',
                      tagName: 'span',
                      title: name,
                    }}
                  />
                  <Image
                    alt=""
                    height={25}
                    loading="lazy"
                    src="/up.svg"
                    width={25}
                  />
                </li>
              ))}
            </ul>
          </article>
        )}
        {negativeEffect?.length > 0 && (
          <article>
            <Paragraph
              {...{ className: 'flex items-center gap-2 pb-2', size: 'large' }}
            >
              <Image
                alt=""
                height={25}
                loading="lazy"
                src="/down.svg"
                width={25}
              />
              {
                translations[
                  'The following adverse effects have also been reported:'
                ]
              }
            </Paragraph>
            <ListSlash {...{ items: negativeEffect }} />
          </article>
        )}
        {effectsExtraInfo && (
          <Paragraph
            {...{
              className: 'max-w-lg',
              dangerouslySetInnerHTML: { __html: effectsExtraInfo },
              tagName: 'article',
              variant: 'medium',
            }}
          />
        )}
      </Wrap>
      {cannabisCounselingCtaPosition === 'middle' && (
        <CannabisCounselingCta {...{ ...cannabisCounselingCta }} />
      )}
      <div {...{ className: 'bg-gray-100 my-10 lg:my-12' }}>
        <Wrap
          {...{
            className: 'gap-y-6 lg:gap-y-8 lg:grid-cols-6 lg:gap-x-10',
            id: sections[2].id,
            size: 'large',
          }}
        >
          <HeadingBody
            {...{
              className: 'lg:col-span-2',
              level: '4',
              tagName: 'h2',
              title: sections[2].name,
            }}
          />
          <HeadingBody
            {...{
              className: 'lg:col-span-3',
              level: '3',
              tagName: 'article',
              title: conditionsDescription,
            }}
          />
          {featuredMedia && (
            <Figure
              {...{
                ...featuredMedia,
                className:
                  'mix-blend-multiply lg:justify-self-end w-32 lg:w-full',
                objectFit: 'contain',
              }}
            />
          )}
          {conditions.map(
            ({ condition: { conditionTypes, title, uri }, description }) => (
              <Card
                {...{
                  className: 'lg:col-span-full lg:grid-cols-3',
                  filled: true,
                  key: title,
                  rounded: true,
                  variant: 'white',
                }}
              >
                <Link {...{ className: 'lg:max-w-xs', href: uri }}>
                  <HeadingBody
                    {...{
                      level: '5',
                      tagName: 'span',
                      title,
                    }}
                  />
                </Link>
                <Paragraph
                  {...{
                    className: 'lg:col-span-2',
                    dangerouslySetInnerHTML: { __html: description },
                    tagName: 'article',
                    variant: 'medium',
                  }}
                />
                {conditionTypes?.length > 0 && (
                  <footer>
                    <p {...{ className: 'sr-only' }}>
                      {translations['Organ Systems']}
                    </p>
                    <ul {...{ className: 'flex flex-wrap' }}>
                      {conditionTypes.map((name, index) => (
                        <React.Fragment {...{ key: index }}>
                          <Paragraph {...{ tagName: 'li', size: 'small' }}>
                            {name}
                          </Paragraph>
                          {index < conditionTypes.length - 1 && (
                            <Paragraph
                              {...{
                                'aria-hidden': true,
                                className: 'pr-1',
                                tagName: 'li',
                                size: 'small',
                              }}
                            >
                              ,
                            </Paragraph>
                          )}
                        </React.Fragment>
                      ))}
                    </ul>
                  </footer>
                )}
              </Card>
            ),
          )}
        </Wrap>
      </div>
      <Wrap
        {...{
          className: 'gap-y-6 lg:gap-y-6',
          id: sections[3].id,
          size: 'large',
        }}
      >
        <HeadingBody
          {...{
            level: '4',
            tagName: 'h2',
            title: sections[3].name,
          }}
        />
        <WysiwygContent
          {...{
            className: 'prose-links-icon',
            content: dosing,
          }}
        />
      </Wrap>
      <Wrap
        {...{
          id: sections[4].id,
          size: 'large',
        }}
      >
        <HeadingBody
          {...{
            className: 'col-span-full',
            level: '4',
            tagName: 'h3',
            title: sections[4].name,
          }}
        />
        <div
          {...{
            className: 'grid gap-y-6 lg:gap-y-6 lg:gap-x-10 lg:grid-cols-2',
          }}
        >
          <Figure
            {...{
              ...growingImage,
              className:
                'max-w-md mx-auto lg:sticky lg:top-14 lg:order-last lg:pt-6 self-start',
              objectFit: 'contain',
            }}
          />
          <WysiwygContent
            {...{
              content: growing,
            }}
          />
        </div>
        <div
          {...{
            className:
              'grid lg:grid-cols-3 gap-inherit lg:gap-x-10 items-start lg:pt-6',
          }}
        >
          <StrainInfo
            {...{
              icon: {
                src: '/height.svg',
                width: 40,
                height: 40,
                alt: 'height icon',
              },
              label: translations['Height'],
              content: growingHeight,
            }}
          />
          <StrainInfo
            {...{
              icon: {
                src: '/humidity.svg',
                width: 40,
                height: 40,
                alt: 'humidity icon',
              },
              label: translations['Humidity and Temperature'],
              content: growingHumidity,
            }}
          />
          <StrainInfo
            {...{
              icon: {
                src: '/cannabis.svg',
                width: 40,
                height: 40,
                alt: 'cannabis icon',
              },
              label: translations['Flowering'],
              content: growingFlowering,
            }}
          />
        </div>
      </Wrap>
      {faqs?.length > 0 ? (
        <Faqs
          sectionId={sections[5].id}
          instanceId={null}
          description={null}
          title={translations['Frequently asked questions']}
          items={faqs.map((faq, index) => ({
            id: `faq_${index}`,
            title: faq.title,
            content: faq.content,
          }))}
        />
      ) : null}
      {relatedStrains?.length > 0 && (
        <Wrap
          {...{
            className: 'sm:grid-cols-3 sm:gap-x-10 gap-y-6 lg:gap-y-6',
            size: 'large',
          }}
        >
          <HeadingBody
            {...{
              className: 'col-span-full',
              level: '5',
              tagName: 'h6',
              title: translations['Related Strains'],
            }}
          />
          {relatedStrains.map((related, key) => (
            <StrainCardRest
              {...{
                ...related,
                key,
                variant: 'medium',
              }}
            />
          ))}
        </Wrap>
      )}
      {cannabisCounselingCtaPosition === 'bottom' && (
        <CannabisCounselingCta {...{ ...cannabisCounselingCta }} />
      )}
    </PageWrap>
  );
};

const SingleStrain: React.FC<SingleStrain> = props => {
  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  return <StrainContent {...props} />;
};

export const getStaticProps: GetStaticProps = async context => {
  return await getSingleStrain(context);
};

export const getStaticPaths: GetStaticPaths = async context => {
  return { paths: [], fallback: 'blocking' };
};

export default SingleStrain;
